import { FieldValues } from 'react-hook-form';
import useApiService from '../utilities/useApiService';
import { formatNdc } from '../utilities/ndc/ndcUtils';
import { useMutation, UseMutationResult } from 'react-query';
import { MutationHandlers } from '../network/models/MutationHandlers';
import { AxiosResponse } from 'axios';
import { useRef } from 'react';

interface MutationVariables {
   returnRequestId: string;
   itemData: FieldValues;
}

export const useAddRequestItem = (
   pharmacyId: string,
   handlers?: MutationHandlers<
      MutationVariables,
      AxiosResponse<any> | undefined
   >
): [
   any | undefined,
   (
      returnRequestId: string,
      itemData: FieldValues,
      handlers?: Omit<
         MutationHandlers<MutationVariables, AxiosResponse<any> | undefined>,
         'onMutate'
      >
   ) => void,
   Omit<UseMutationResult<any, unknown, MutationVariables>, 'data' | 'mutate'>
] => {
   const { postMethod } = useApiService();

   const { data, mutate, ...state } = useMutation({
      mutationKey: [
         `POST:/pharmacies/${pharmacyId}/returnrequests/{returnRequestId}/items`,
      ],
      mutationFn: async ({ returnRequestId, itemData }: MutationVariables) => {
         if (!returnRequestId) return undefined;
         if (!itemData) return undefined;

         const formattedNdc = formatNdc(itemData.ndc);
         return postMethod(
            `/pharmacies/${pharmacyId}/returnrequests/${returnRequestId}/items`,
            {
               ndc: formattedNdc,
               description: itemData.description,
               name: 'name',
               manufacturer: itemData.manufacturer,
               packageSize: itemData.pkgSize,
               gtin14: itemData.gtin14,
               serialNumber: itemData.serialNumber,
               controlledSubstanceCode: itemData.controlledSubstanceCode,
               requestType: 'typeofrequest',
               fullQuantity:
                  itemData.quantityType !== 'partial' ? itemData.quantity : '0',
               partialQuantity:
                  itemData.quantityType === 'partial' ? itemData.quantity : '0',
               strength: itemData.strength,
               dosage: itemData.dosage,
               status: 'PENDING',
               expirationDate: itemData.expDate,
               lotNumber: itemData.lot,
               isFullQuantityOpened: itemData.quantityType === 'fullOpened',
            }
         );
      },
      onError: handlers?.onError,
      onSuccess: handlers?.onSuccess,
      onSettled: handlers?.onSettled,
      onMutate: handlers?.onMutate,
   });

   return [
      data,
      (
         returnRequestId: string,
         itemData: FieldValues,
         handlers?: Omit<
            MutationHandlers<MutationVariables, AxiosResponse<any> | undefined>,
            'onMutate'
         >
      ) =>
         mutate(
            { returnRequestId, itemData },
            {
               onError: handlers?.onError,
               onSuccess: handlers?.onSuccess,
               onSettled: handlers?.onSettled,
            }
         ),
      state,
   ];
};
